import React, { useEffect, useState, useCallback } from "react";
import { fetchOhioEmployerDetail } from "../../../services/influencedPlacementService";
import { fetchLinkedinExperience } from "../../../services/candidateService";
import { OhioEmployerDetailResponse, LinkedInExperienceResponse } from "../../../types/fyo";
import PageHeader from "../../shared/PageHeader/PageHeader";
import Badge from "../../shared/Badge/Badge";
import { formatDateLong } from "../../../utils/formatters";
import "./InfluencedPlacementDetailPage.css";

interface Props {
  candidateId:     string | number;
  onBack:          () => void;
  onViewCandidate: (id: number) => void;
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fyo-detail-field">
      <span className="fyo-detail-field__label">{label}</span>
      <div className="fyo-detail-field__value">{children}</div>
    </div>
  );
}

export default function InfluencedPlacementDetailPage({ candidateId, onBack, onViewCandidate }: Props) {
  const [data,       setData]       = useState<OhioEmployerDetailResponse | null>(null);
  const [experience, setExperience] = useState<LinkedInExperienceResponse | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchOhioEmployerDetail(candidateId)
      .then((d) => {
        setData(d);
        return fetchLinkedinExperience(d.candidate.rms_candidate_id);
      })
      .then(setExperience)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const cand = data?.candidate;

  return (
    <div className="fyo-page">
      <button className="fyo-back-btn" onClick={onBack}>← Back to LinkedIn Tracked Ohio Hires</button>
      <PageHeader
        title={cand ? cand.display_name : "Placement Detail"}
        subtitle="LinkedIn Tracked Ohio Hires"
      />

      {error && <div className="fyo-error-banner">{error}</div>}

      {loading ? (
        <p className="state-text state-text--muted">Loading...</p>
      ) : data ? (
        <div className="fyo-refdetail__layout">

          {/* ── Candidate Card ── */}
          <div className="fyo-card">
            <h2 className="fyo-section-title">Candidate</h2>
            <div className="fyo-detail-grid">
              <Field label="Name">
                <button className="fyo-link-btn" onClick={() => onViewCandidate(cand!.rms_candidate_id)}>
                  {cand!.display_name}
                </button>
              </Field>
              <Field label="Email">{cand!.email || "—"}</Field>
              <Field label="LinkedIn">
                {cand!.linkedin_url
                  ? <a href={cand!.linkedin_url} target="_blank" rel="noopener noreferrer" className="fyo-link-btn">View Profile</a>
                  : <span className="fyo-muted">—</span>}
              </Field>
              <Field label="Job Title">{cand!.job_title || "—"}</Field>
              <Field label="Current Employer">{cand!.current_employer || "—"}</Field>
              <Field label="Location">
                {[cand!.town, cand!.state, cand!.country].filter(Boolean).join(", ") || "—"}
              </Field>
            </div>
          </div>

          {/* ── Tracking Info ── */}
          <div className="fyo-card">
            <h2 className="fyo-section-title">Tracking Info</h2>
            <div className="fyo-detail-grid" style={{ marginBottom: "var(--space-5)" }}>
              <Field label="Tracking Start">{formatDateLong(data.tracking_window_start)}</Field>
              <Field label="Tracking End">{formatDateLong(data.tracking_window_end)}</Field>
              <Field label="Status"><Badge text={data.status} /></Field>
              <Field label="Last Updated">{formatDateLong(data.updated_at)}</Field>
            </div>

          </div>

          {/* ── Result Section ── */}
          {(data.status === "detected" || data.status === "not_detected") && (
            <div className="fyo-card">
              <h2 className="fyo-section-title">Result</h2>
              <div className="fyo-detail-grid">
                <Field label="Ohio Employer Found">
                  <div className="fyo-detail-field__value-row">
                    <Badge text={data.status === "detected" ? "detected" : "not_detected"} />
                    {data.status !== "detected" && (
                      <span style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-tertiary)", fontWeight: 500 }}>Window Expired</span>
                    )}
                  </div>
                </Field>
              </div>
            </div>
          )}

          {/* ── LinkedIn Experience ── */}
          <div className="fyo-card">
            <h2 className="fyo-section-title">LinkedIn Experience</h2>
            {!experience || experience.experience.length === 0 ? (
              <div className="fyo-empty-card">No LinkedIn experience scraped yet.</div>
            ) : (
              <div className="fyo-table-wrap">
              <table className="fyo-table fyo-table--compact">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Position</th>
                    <th>Location</th>
                    <th>Period</th>
                  </tr>
                </thead>
                <tbody>
                  {experience.experience.map((e, i) => {
                    return (
                      <tr key={i} className={i % 2 === 1 ? "fyo-table__row--odd" : ""}>
                        <td className="fyo-table__cell--primary">{e.company}</td>
                        <td>{e.position}</td>
                        <td>{e.location || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {e.start_date || "?"} → {e.end_date || "Present"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>

        </div>
      ) : null}
    </div>
  );
}
